import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Button,
  Descriptions,
  Modal,
  Statistic,
  Row,
  Col,
  message,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import {
  fetchVerificationCodes,
  fetchVerificationCodeDetail,
  fetchVerificationCodeStatistics,
  setFilters,
  setPagination,
  clearError,
  clearCodeDetail,
} from '../../features/verificationCode/verificationCodeSlice';
import { formatDateTime } from '../../utils/index';

const { RangePicker } = DatePicker;
const { Option } = Select;

const VerificationCodeList = () => {
  const dispatch = useDispatch();
  const { codes, pagination, filters, isLoading, error, codeDetail, statistics } = useSelector(
    (state) => state.verificationCode
  );

  const [detailVisible, setDetailVisible] = useState(false);
  const [statisticsVisible, setStatisticsVisible] = useState(false);

  useEffect(() => {
    dispatch(fetchVerificationCodes({ ...filters, ...pagination }));
    dispatch(fetchVerificationCodeStatistics());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 搜索处理
  const handleSearch = () => {
    dispatch(setPagination({ current: 1 }));
    dispatch(fetchVerificationCodes({ ...filters, ...pagination, current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
    dispatch(fetchVerificationCodes({ ...pagination, current: 1 }));
  };

  // 查看详情
  const handleViewDetail = async (id) => {
    await dispatch(fetchVerificationCodeDetail(id));
    setDetailVisible(true);
  };

  // 表格列配置
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '场景',
      dataIndex: 'scene',
      key: 'scene',
      width: 120,
      render: (scene) => {
        const sceneNameMap = {
          register: '注册',
          login: '登录',
          password_reset: '重置密码',
          pay_password: '支付密码',
          withdraw: '提现',
          other: '其他',
        };
        const colorMap = {
          register: 'blue',
          login: 'green',
          password_reset: 'orange',
          pay_password: 'purple',
          withdraw: 'cyan',
          other: 'default',
        };
        return (
          <Tag color={colorMap[scene] || 'default'}>
            {sceneNameMap[scene] || scene}
          </Tag>
        );
      },
    },
    {
      title: '通道',
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel) => {
        const channelNameMap = {
          sms: '短信',
          email: '邮件',
        };
        const colorMap = {
          sms: 'blue',
          email: 'green',
        };
        return (
          <Tag color={colorMap[channel] || 'default'}>
            {channelNameMap[channel] || channel}
          </Tag>
        );
      },
    },
    {
      title: '接收方',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 150,
    },
    {
      title: '验证码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      render: (code) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{code}</span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusNameMap = {
          pending: '待发送',
          sent: '已发送',
          used: '已使用',
          expired: '已过期',
          failed: '发送失败',
        };
        const colorMap = {
          pending: 'default',
          sent: 'processing',
          used: 'success',
          expired: 'warning',
          failed: 'error',
        };
        return (
          <Tag color={colorMap[status] || 'default'}>
            {statusNameMap[status] || status}
          </Tag>
        );
      },
    },
    {
      title: '供应商',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 120,
    },
    {
      title: '过期时间',
      dataIndex: 'expire_at',
      key: 'expire_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '使用时间',
      dataIndex: 'used_at',
      key: 'used_at',
      width: 180,
      render: (text) => (text ? formatDateTime(text) : '-'),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record.id)}
        >
          查看
        </Button>
      ),
    },
  ];

  // 表格变化处理
  const handleTableChange = (newPagination) => {
    dispatch(setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
    dispatch(fetchVerificationCodes({
      ...filters,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  return (
    <div>
      <Card>
        {/* 搜索区域 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="场景"
            allowClear
            style={{ width: 150 }}
            value={filters.scene}
            onChange={(value) => dispatch(setFilters({ ...filters, scene: value }))}
          >
            <Option value="register">注册</Option>
            <Option value="login">登录</Option>
            <Option value="password_reset">重置密码</Option>
            <Option value="pay_password">支付密码</Option>
            <Option value="withdraw">提现</Option>
            <Option value="other">其他</Option>
          </Select>

          <Select
            placeholder="通道"
            allowClear
            style={{ width: 120 }}
            value={filters.channel}
            onChange={(value) => dispatch(setFilters({ ...filters, channel: value }))}
          >
            <Option value="sms">短信</Option>
            <Option value="email">邮件</Option>
          </Select>

          <Select
            placeholder="状态"
            allowClear
            style={{ width: 120 }}
            value={filters.status}
            onChange={(value) => dispatch(setFilters({ ...filters, status: value }))}
          >
            <Option value="pending">待发送</Option>
            <Option value="sent">已发送</Option>
            <Option value="used">已使用</Option>
            <Option value="expired">已过期</Option>
            <Option value="failed">发送失败</Option>
          </Select>

          <Input
            placeholder="接收方"
            style={{ width: 200 }}
            value={filters.recipient}
            onChange={(e) => dispatch(setFilters({ ...filters, recipient: e.target.value }))}
            allowClear
          />

          <RangePicker
            onChange={(dates) => {
              if (dates) {
                dispatch(setFilters({
                  ...filters,
                  startDate: dates[0].format('YYYY-MM-DD'),
                  endDate: dates[1].format('YYYY-MM-DD'),
                }));
              } else {
                const newFilters = { ...filters };
                delete newFilters.startDate;
                delete newFilters.endDate;
                dispatch(setFilters(newFilters));
              }
            }}
          />

          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            搜索
          </Button>

          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            重置
          </Button>

          <Button
            icon={<BarChartOutlined />}
            onClick={() => {
              dispatch(fetchVerificationCodeStatistics(filters));
              setStatisticsVisible(true);
            }}
          >
            统计
          </Button>
        </Space>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={codes}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          scroll={{ x: 1500 }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="验证码详情"
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          dispatch(clearCodeDetail());
        }}
        footer={null}
        width={800}
      >
        {codeDetail && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="ID">{codeDetail.id}</Descriptions.Item>
            <Descriptions.Item label="场景">
              <Tag>{codeDetail.sceneName || codeDetail.scene}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="通道">
              <Tag>{codeDetail.channelName || codeDetail.channel}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="接收方">{codeDetail.recipient}</Descriptions.Item>
            <Descriptions.Item label="验证码">
              <span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 16 }}>
                {codeDetail.code}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={codeDetail.statusColor}>{codeDetail.statusName || codeDetail.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="供应商">{codeDetail.vendor || '-'}</Descriptions.Item>
            <Descriptions.Item label="请求ID">{codeDetail.request_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="过期时间">
              {formatDateTime(codeDetail.expire_at)}
            </Descriptions.Item>
            <Descriptions.Item label="使用时间">
              {codeDetail.used_at ? formatDateTime(codeDetail.used_at) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatDateTime(codeDetail.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="失败原因">
              {codeDetail.fail_reason || '-'}
            </Descriptions.Item>
            {codeDetail.payload && Object.keys(codeDetail.payload).length > 0 && (
              <Descriptions.Item label="额外参数" span={2}>
                <pre style={{ margin: 0 }}>
                  {JSON.stringify(codeDetail.payload, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* 统计弹窗 */}
      <Modal
        title="验证码统计"
        open={statisticsVisible}
        onCancel={() => setStatisticsVisible(false)}
        footer={null}
        width={800}
      >
        {statistics && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic title="总计" value={statistics.total} />
              </Col>
              <Col span={6}>
                <Statistic title="今日发送" value={statistics.todayStats?.sent || 0} />
              </Col>
              <Col span={6}>
                <Statistic title="今日使用" value={statistics.todayStats?.used || 0} />
              </Col>
              <Col span={6}>
                <Statistic title="今日失败" value={statistics.todayStats?.failed || 0} />
              </Col>
            </Row>

            <Card title="按状态统计" size="small" style={{ marginBottom: 16 }}>
              <Space wrap>
                {statistics.statusStats?.map((item) => (
                  <Tag key={item.status} color={
                    item.status === 'sent' ? 'processing' :
                    item.status === 'used' ? 'success' :
                    item.status === 'failed' ? 'error' : 'default'
                  }>
                    {item.status}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>

            <Card title="按场景统计" size="small" style={{ marginBottom: 16 }}>
              <Space wrap>
                {statistics.sceneStats?.map((item) => (
                  <Tag key={item.scene} color="blue">
                    {item.scene}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>

            <Card title="按通道统计" size="small">
              <Space wrap>
                {statistics.channelStats?.map((item) => (
                  <Tag key={item.channel} color={item.channel === 'sms' ? 'blue' : 'green'}>
                    {item.channel}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VerificationCodeList;

